import { exhaustiveFail } from '@cbridge-utils/error';
import {
  IApiClientSymbol, IApiClientCoreSymbol, IApiClientSymbolServer, IApiClientCoreSymbolDetails, IApiNodeClientSymbolDetails,
  IApiServerClientSymbolDetails, IApiClientSymbolMarkup, IApiClientSymbolLp, IApiClientSymbolOrderLifetimeProfile,
  IApiClientSymbolAdjustmentProfile, IApiClientSymbolPriceFilteringProfile, IApiClientSymbolPricingProfile, IApiSymbolClientRoutingRule,
  ApiClientSymbolInclude, IApiClientSymbolCreate, IApiServerClientSymbol, ApiExecutionType, ApiClientSymbolFilter,
  IApiClientSymbolSpread,
} from '@cbridge-packages/api';
import { ServerTranslator } from '@cbridge-packages/server';
import { ScheduleTranslator } from '@cbridge-packages/schedule';
import { StreamTranslator } from '@cbridge-packages/stream';
import { ISymbol, SymbolTranslator } from '@cbridge-packages/symbol';
import { MarkupProfileTranslator } from '@cbridge-packages/markup-profile';
import { MarkupTranslator } from '@cbridge-packages/markup';
import { PricingProfileTranslator } from '@cbridge-packages/pricing-profile';
import { INode } from '@cbridge-packages/node';
import { toUiRuleRegexp } from '@cbridge-packages/rule';
import type { ISpreadProfile } from '@cbridge-packages/spread-profile';
import {
  ISymbolClient, ISymbolClientDetails, ISymbolClientServer, ISymbolClientMarkup, ISymbolClientOrderLifetimeProfile,
  ISymbolClientAdjustmentProfile, ISymbolClientPriceFilteringProfile, ISymbolClientPricingProfile, ISymbolClientLpSymbolMarkup,
  ISymbolClientLpSymbolValidation, ISymbolClientLp, ISymbolClientRoutingRule, ISymbolClientForm, ClientSymbolFilter, ISymbolClientInServer,
  ITradableSymbolClient, ISymbolClientWithPricing, ISymbolClientWithRouting, ExecutionType, ISymbolClientClientDetails,
} from './symbolClient.interface';

export namespace SymbolClientTranslator {
  export function fromApiCoreSymbol(coreSymbol: IApiClientCoreSymbol): Partial<ISymbol> {
    return {
      symbolId: coreSymbol.id,
      name: coreSymbol.name,
      symbolGroup: coreSymbol.symbolGroup,
      baseAsset: coreSymbol.baseAsset,
      quoteAsset: coreSymbol.quoteAsset,
      throttlingInterval: coreSymbol.throttlingInterval,
    };
  }

  export function fromApiNode(symbol: IApiClientSymbol): ISymbolClient {
    const { coreSymbol, name, stream, schedule } = symbol;

    return {
      coreSymbol: fromApiCoreSymbol(coreSymbol),
      clientSymbolName: name,
      stream: stream && StreamTranslator.fromApiBase(stream),
      scheduleName: schedule?.name,
    };
  }

  export function fromApiServer(symbol: IApiServerClientSymbol): ISymbolClientInServer {
    const clientSymbol: ITradableSymbolClient = {
      ...SymbolClientTranslator.fromApiNode(symbol),
      coreSymbol: SymbolTranslator.fromApi(symbol.coreSymbol),
      isTradable: symbol.isTradable,
      throttlingIntervalServer: symbol.throttlingIntervalServer,
      throttlingIntervalCoreSymbol: symbol.throttlingIntervalCoreSymbol,
    };

    if ('adjustmentProfile' in symbol) {
      const { lpMultipliers } = symbol;
      const adjustmentProfile = SymbolClientTranslator.fromSymbolAdjustmentProfileApi(symbol.adjustmentProfile);

      if ('priceFilteringProfile' in symbol) {
        const { markup, priceFilteringProfile, lpMarkups, spread } = symbol;
        const clientSymbolWithPricing: ISymbolClientWithPricing = {
          ...clientSymbol,
          adjustmentProfile,
          priceFilteringProfile,
          ...(spread && { spread }),
        };
        if (lpMultipliers) {
          clientSymbolWithPricing.lpMultipliers = lpMultipliers;
        }
        if (markup) {
          clientSymbolWithPricing.markup = SymbolClientTranslator.fromSymbolMarkupApi(markup);
        }
        if (lpMarkups) {
          clientSymbolWithPricing.lpMarkups = {
            bid: lpMarkups.bid,
            ask: lpMarkups.ask,
            type: MarkupTranslator.fromApiMarkupType(lpMarkups.type),
          };
        }
        if ('executionType' in symbol) {
          const { executionType, pricingMode, lpVolumeValidation } = symbol;
          Object.assign(clientSymbolWithPricing, {
            executionType: SymbolClientTranslator.fromApiExecutionType(executionType),
            ...(pricingMode && { pricingMode: PricingProfileTranslator.fromApiPricingMode(pricingMode) }),
            ...(lpVolumeValidation && { lpVolumeValidation }),
          });
        }
        return clientSymbolWithPricing;
      }
      if ('executionType' in symbol) {
        const { executionType, pricingMode, lpVolumeValidation } = symbol;
        const clientSymbolWithRouting: ISymbolClientWithRouting = {
          ...clientSymbol,
          adjustmentProfile,
          executionType: SymbolClientTranslator.fromApiExecutionType(executionType),
        };

        if (pricingMode) {
          clientSymbolWithRouting.pricingMode = PricingProfileTranslator.fromApiPricingMode(pricingMode);
        }
        if (lpVolumeValidation) {
          clientSymbolWithRouting.lpVolumeValidation = lpVolumeValidation;
        }
        return clientSymbolWithRouting;
      }
    }

    return clientSymbol;
  }

  export function fromApiExecutionType(type: ApiExecutionType): ExecutionType {
    switch (type) {
      case ApiExecutionType.BookA:
        return ExecutionType.BookA;
      case ApiExecutionType.BookB:
        return ExecutionType.BookB;
      case ApiExecutionType.Reject:
        return ExecutionType.Reject;
      default:
        return exhaustiveFail(type);
    }
  }

  export function toApi(symbol: ISymbolClientForm): IApiClientSymbolCreate {
    return {
      coreSymbolId: symbol.coreSymbolId,
      name: symbol.name,
      streamId: symbol.streamId,
    };
  }

  export function toForm(nodeId: INode['nodeId'], symbol: ISymbolClient): ISymbolClientForm {
    return {
      nodeId,
      coreSymbolId: symbol?.coreSymbol.symbolId,
      streamId: symbol?.stream?.streamId,
      name: symbol?.clientSymbolName,
    };
  }

  function fromApiClient(client: IApiClientSymbolServer['pricingClient']): ISymbolClientClientDetails {
    if (!client) {
      return undefined;
    }

    return {
      clientId: client.id,
      enabled: client.enabled,
    };
  }

  export function fromSymbolServerApi(server: IApiClientSymbolServer): ISymbolClientServer {
    return {
      serverId: server.id,
      serverName: server.name,
      type: ServerTranslator.fromApiServerType(server.type),
      pricingClient: fromApiClient(server.pricingClient),
      tradingClient: fromApiClient(server.tradingClient),
      throttlingInterval: server.throttlingInterval,
    };
  }

  export function fromSymbolLpApi(lp: IApiClientSymbolLp): ISymbolClientLp {
    const { lpSymbol } = lp;

    return {
      id: lp.id,
      name: lp.name,
      lpSymbol: lpSymbol
        ? {
          name: lpSymbol.name,
          priceMultiplier: lpSymbol.priceMultiplier,
          volumeMultiplier: lpSymbol.volumeMultiplier,
          bidMarkup: lpSymbol.bidMarkup,
          askMarkup: lpSymbol.askMarkup,
          markupType: MarkupTranslator.fromApiMarkupType(lpSymbol.markupType),
          minVolume: lpSymbol.minVolume,
          maxVolume: lpSymbol.maxVolume,
          stepVolume: lpSymbol.stepVolume,
        }
        : undefined,
    };
  }

  export function fromLpApiToSymbolMarkup(lp: IApiClientSymbolLp): ISymbolClientLpSymbolMarkup {
    const { lpSymbol } = lp;

    return {
      lpName: lp.name,
      lpSymbolName: lpSymbol?.name,
      bidMarkup: lpSymbol?.bidMarkup,
      askMarkup: lpSymbol?.askMarkup,
    };
  }

  export function fromLpApiToSymbolValidation(lp: IApiClientSymbolLp): ISymbolClientLpSymbolValidation {
    const { lpSymbol } = lp;

    return {
      lpName: lp.name,
      lpSymbolName: lpSymbol?.name,
      minVolume: lpSymbol?.minVolume,
      maxVolume: lpSymbol?.maxVolume,
      stepVolume: lpSymbol?.stepVolume,
    };
  }

  export function fromSymbolRoutingRuleApi(routingRule: IApiSymbolClientRoutingRule): ISymbolClientRoutingRule {
    return {
      accounts: routingRule.accounts,
      accountRegexp: toUiRuleRegexp(routingRule.accountRegexp),
      accountGroups: routingRule.accountGroups,
      accountGroupRegexp: toUiRuleRegexp(routingRule.accountGroupRegexp),
      executionType: SymbolClientTranslator.fromApiExecutionType(routingRule.executionType),
    };
  }

  export function fromSymbolMarkupApi(markup: IApiClientSymbolMarkup): ISymbolClientMarkup {
    return {
      profile: MarkupProfileTranslator.fromApi(markup.profile),
      askMarkup: markup.askMarkup,
      bidMarkup: markup.bidMarkup,
      type: MarkupTranslator.fromApiMarkupType(markup.type),
    };
  }

  export function fromSymbolOrderLifetimeProfileApi(profile: IApiClientSymbolOrderLifetimeProfile): ISymbolClientOrderLifetimeProfile {
    return {
      orderLifetimeId: profile?.id,
      name: profile?.name,
      limitFokTimeout: profile.limitFokTimeout,
      limitIocTimeout: profile.limitIocTimeout,
      limitGtcTimeout: profile.limitGtcTimeout,
      marketFokTimeout: profile.marketFokTimeout,
      marketIocTimeout: profile.marketIocTimeout,
      marketGtcTimeout: profile.marketGtcTimeout,
    };
  }

  export function fromSymbolAdjustmentProfileApi(profile: IApiClientSymbolAdjustmentProfile): ISymbolClientAdjustmentProfile {
    return {
      adjustmentId: profile?.id,
      name: profile?.name,
      priceMultiplier: profile.priceMultiplier,
      volumeMultiplier: profile.volumeMultiplier,
    };
  }

  export function fromSymbolPriceFilteringProfileApi(profile: IApiClientSymbolPriceFilteringProfile): ISymbolClientPriceFilteringProfile {
    return {
      priceFilteringId: profile?.id,
      name: profile?.name,
      maxClientMarketDepth: profile.maxClientMarketDepth,
      maxBookbMarketDepth: profile.maxBookbMarketDepth,
    };
  }

  export function fromSymbolPricingProfileApi(profile: IApiClientSymbolPricingProfile): ISymbolClientPricingProfile {
    return {
      id: profile?.id,
      name: profile?.name,
      mode: PricingProfileTranslator.fromApiPricingMode(profile.mode),
      aggregationMethod: profile.aggregationMethod
        ? PricingProfileTranslator.fromApiAggregationMethod(profile.aggregationMethod)
        : undefined,
    };
  }

  export function fromSymbolSpreadApi(spread: IApiClientSymbolSpread): ISpreadProfile {
    return {
      id: spread.profile.id,
      name: spread.profile.name,
      spreads: spread.spreads,
      applyMaxToLower: spread.applyMaxToLower,
    };
  }

  export function fromCoreSymbolDetailsApi(coreSymbol: IApiClientCoreSymbolDetails): Partial<ISymbol> {
    return {
      symbolId: coreSymbol.id,
      name: coreSymbol.name,
      symbolGroup: coreSymbol.symbolGroup,
      baseAsset: coreSymbol.baseAsset,
      quoteAsset: coreSymbol.quoteAsset,
      priceDigits: coreSymbol.priceDigits,
      volumeDigits: coreSymbol.volumeDigits,
      throttlingInterval: coreSymbol.throttlingInterval,
    };
  }

  export function fromNodeDetailsApi(symbolNodeDetails: IApiNodeClientSymbolDetails): ISymbolClientDetails {
    const lpsWithSymbols = symbolNodeDetails.lps?.filter(({ lpSymbol }) => Boolean(lpSymbol));

    return {
      coreSymbol: fromCoreSymbolDetailsApi(symbolNodeDetails.coreSymbol),
      clientSymbolName: symbolNodeDetails.name,
      stream: StreamTranslator.fromApiBase(symbolNodeDetails.stream),
      schedule: symbolNodeDetails.schedule
        ? ScheduleTranslator.fromApiFullView(symbolNodeDetails.schedule)
        : undefined,
      servers: symbolNodeDetails.servers?.map(fromSymbolServerApi),
      lps: symbolNodeDetails.lps?.map(fromSymbolLpApi),
      markup: symbolNodeDetails.markup && fromSymbolMarkupApi(symbolNodeDetails.markup),
      lpSymbolMarkups: lpsWithSymbols?.map(fromLpApiToSymbolMarkup),
      lpSymbolValidations: lpsWithSymbols?.map(fromLpApiToSymbolValidation),
      node: symbolNodeDetails.node && {
        value: symbolNodeDetails.node.id,
        label: symbolNodeDetails.node.name,
      },
      orderLifetimeProfile: undefined,
      adjustmentProfile: undefined,
      priceFilteringProfile: undefined,
      routingRules: undefined,
      pricingProfile: undefined,
      spreadProfile: symbolNodeDetails.spread ? fromSymbolSpreadApi(symbolNodeDetails.spread) : undefined,
    };
  }

  export function fromServerDetailsApi(symbolServerDetails: IApiServerClientSymbolDetails): ISymbolClientDetails {
    const lpsWithSymbols = symbolServerDetails.lps?.filter(({ lpSymbol }) => Boolean(lpSymbol));

    return {
      coreSymbol: fromCoreSymbolDetailsApi(symbolServerDetails.coreSymbol),
      clientSymbolName: symbolServerDetails.name,
      stream: StreamTranslator.fromApiBase(symbolServerDetails.stream),
      schedule: symbolServerDetails.schedule
        ? ScheduleTranslator.fromApiFullView(symbolServerDetails.schedule)
        : undefined,
      servers: symbolServerDetails.servers.map(fromSymbolServerApi),
      lps: symbolServerDetails.lps?.map(fromSymbolLpApi),
      markup: symbolServerDetails.markup && fromSymbolMarkupApi(symbolServerDetails.markup),
      lpSymbolMarkups: lpsWithSymbols?.map(fromLpApiToSymbolMarkup),
      lpSymbolValidations: lpsWithSymbols?.map(fromLpApiToSymbolValidation),
      node: symbolServerDetails.node && {
        value: symbolServerDetails.node.id,
        label: symbolServerDetails.node.name,
      },
      orderLifetimeProfile: fromSymbolOrderLifetimeProfileApi(symbolServerDetails.orderLifetimeProfile),
      adjustmentProfile: fromSymbolAdjustmentProfileApi(symbolServerDetails.adjustmentProfile),
      priceFilteringProfile: fromSymbolPriceFilteringProfileApi(symbolServerDetails.priceFilteringProfile),
      routingRules: symbolServerDetails.routingRules?.map(fromSymbolRoutingRuleApi),
      pricingProfile: symbolServerDetails.pricingProfile
        ? fromSymbolPricingProfileApi(symbolServerDetails.pricingProfile)
        : undefined,
      spreadProfile: symbolServerDetails.spread ? fromSymbolSpreadApi(symbolServerDetails.spread) : undefined,
    };
  }

  export function toApiFilters(
    filter?: ClientSymbolFilter,
  ): ApiClientSymbolFilter | undefined {
    if (!filter) {
      return undefined;
    }

    switch (filter) {
      case ClientSymbolFilter.NonTradable:
        return ApiClientSymbolFilter.NonTradable;
      case ClientSymbolFilter.Tradable:
        return ApiClientSymbolFilter.Tradable;
      default:
        return undefined;
    }
  }

  export function toApiIncludeDefault(): string {
    return `${ApiClientSymbolInclude.Pricing},${ApiClientSymbolInclude.Routing}`;
  }

  export function toApiSort(sort: string[]): string {
    if (sort?.length > 0) {
      return sort.join(',');
    }

    return undefined;
  }

  export function toApiIncludesUnmapped(filter?: ClientSymbolFilter): boolean | undefined {
    if (filter === ClientSymbolFilter.Unmapped) {
      return true;
    }
    return undefined;
  }

}
